import {
  openNotificationStream as openStream,
  type NotificationStreamHandlers,
} from '@gokrew/ui/utility/notificationStream';
import { buildNotificationActionBody } from '@gokrew/ui/utility/notifications';
import api from './client';
import { tokenManager } from '@/auth';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  DeleteNotificationResponse,
  GetNotificationsParams,
  MarkAllNotificationsReadResponse,
  MarkNotificationReadResponse,
  NotificationActionRequest,
  NotificationFeed,
  NotificationUnreadCount,
} from '@gokrew/models/notifications';

const base = (companyId: string) => `/companies/${companyId}/notifications`;

const endpoints = {
  feed: (companyId: string) => base(companyId),
  unreadCount: (companyId: string) => `${base(companyId)}/unread-count`,
  stream: (companyId: string) => `${base(companyId)}/stream`,
  markRead: (companyId: string, id: string) => `${base(companyId)}/${id}/read`,
  markAllRead: (companyId: string) => `${base(companyId)}/read-all`,
  remove: (companyId: string, id: string) => `${base(companyId)}/${id}`,
};

/* ── Feed ────────────────────────────────────────────────────────────────── */

export const getNotifications = async (
  companyId: string,
  params: GetNotificationsParams = {}
) => {
  const { data } = await api.get<ApiResponse<NotificationFeed>>(
    endpoints.feed(companyId),
    { params }
  );
  return data.data;
};

export const getNotificationsUnreadCount = async (companyId: string) => {
  const { data } = await api.get<ApiResponse<NotificationUnreadCount>>(
    endpoints.unreadCount(companyId)
  );
  return data.data;
};

export const markNotificationRead = async (companyId: string, id: string) => {
  const { data } = await api.patch<ApiResponse<MarkNotificationReadResponse>>(
    endpoints.markRead(companyId, id)
  );
  return data.data;
};

export const markAllNotificationsRead = async (companyId: string) => {
  const { data } = await api.patch<
    ApiResponse<MarkAllNotificationsReadResponse>
  >(endpoints.markAllRead(companyId));
  return data.data;
};

export const deleteNotification = async (companyId: string, id: string) => {
  const { data } = await api.delete<ApiResponse<DeleteNotificationResponse>>(
    endpoints.remove(companyId, id)
  );
  return data.data;
};

/* ── Inline actions ──────────────────────────────────────────────────────── */

/** Fires an action's own resolved request, at the path the action carries. */
export const runNotificationAction = async (
  request: NotificationActionRequest,
  promptValue?: string
) => {
  const { data } = await api.request<ApiResponse<unknown>>({
    method: request.method,
    url: request.path,
    data: buildNotificationActionBody(request, promptValue),
  });
  return data.data;
};

/* ── SSE stream ──────────────────────────────────────────────────────────── */

/**
 * No language is threaded in: the stream endpoint takes none, and the copy on
 * its frames is never rendered — the panel re-reads the feed over REST, where
 * the interceptor sends the reader's language.
 */
export const openNotificationStream = (
  companyId: string,
  signal: AbortSignal,
  handlers: NotificationStreamHandlers
) =>
  openStream(
    `/api/v1${endpoints.stream(companyId)}`,
    () => tokenManager.getValidToken(),
    signal,
    handlers
  );
